import find from 'lodash/find';
import { useNavigate } from 'react-router-dom';
import { Form } from '@codehesion-za/headless';
import { FormikProps } from 'formik/dist/types';
import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Card,
  PaymentAmountField,
  PrayerRequestCheckboxes,
  RecaptchaComponent,
  TextField,
} from '@project/components';
import { MeetingContributionValues, MeetingContributionProps } from './types';
import { meetingContributionSchema } from './schemas';
import {
  useGetEventQuery,
  useGetMultipleExchangeCurrencyQuery,
  useGetPublicEventQuery,
} from '@project/queries';
import { ROBOT_CHECK_PASS } from '@project/helper';
import { useSessionStorage } from '@project/hooks';

export const MeetingContributionForm: React.FC<MeetingContributionProps> = ({
  id,
  indemnityFormId,
  eventType,
  isPublic = false,
}) => {
  const navigate = useNavigate();
  const { data: event } = isPublic ? useGetPublicEventQuery(id) : useGetEventQuery(id);
  const [roboCheck, setRebotCheck] = useSessionStorage(ROBOT_CHECK_PASS, false);

  const defaultMinDonation = event?.minDonations.find(
    (option) => option && option.currency === 'USD',
  )?.minDonation;

  const initiaValues: MeetingContributionValues = {
    contributionCurrency: event?.minDonationCurrency ?? 'USD',
    contributionAmount: defaultMinDonation ? Number(defaultMinDonation) : 0.0,
    prayerRequests: [],
    tip: 0,
    sponsor_magazine: 0,
    paymentMethod: 'debit_credit',
  };

  const currentMinDonation = (currency?: string) =>
    find(event?.minDonations, (minDonation) => minDonation.currency === currency) ?? {
      symbol: 'R',
      currency: 'ZAR',
      minDonation: '0.00',
    };

  const currentMinDonationAmount = (currency?: string) =>
    Number(currentMinDonation(currency)?.minDonation);

  const [selectedMinDonation, setSelectedMinDonation] = useState(
    currentMinDonation(event?.minDonationCurrency ?? ''),
  );

  const schema = useMemo(
    () => meetingContributionSchema(currentMinDonationAmount(selectedMinDonation?.currency)),
    [selectedMinDonation],
  );

  const onSubmitForm = (formData: MeetingContributionValues) => {
    navigate(
      `/payment?type=${event?.name}&event_id=${event?.id}&amount=${
        formData.contributionAmount
      }&tipAmount=${formData.tip ?? 0}&currency=${
        formData.contributionCurrency
      }&payment_type=event&prayer_requests=${
        formData.prayerRequests
      }&indemnity_form_id=${indemnityFormId}&event_type=${eventType}&sponsor_magazine=${
        formData.sponsor_magazine ?? 0
      }&is_recuring=${event?.isRecuring}&payment_method=${formData.paymentMethod}`,
    );
    return new Promise(() => null);
  };

  const FormComponents = ({
    values,
    isSubmitting,
    handleSubmit,
    setFieldError,
  }: FormikProps<MeetingContributionValues>) => {
    useEffect(() => {
      setSelectedMinDonation(currentMinDonation(values.contributionCurrency));
    }, [values.contributionCurrency]);

    const {
      data: exchangeAmount,
      refetch: refetchMultipleCurrencyExchange,
      isFetching: isLoaddingExchangeRate,
    } = useGetMultipleExchangeCurrencyQuery({
      tip: {
        amount: values.tip || 0,
        requested_currency: 'usd',
        amount_currency: values.contributionCurrency,
      },
      sponsor_magazine: {
        amount: values.sponsor_magazine || 0,
        requested_currency: 'usd',
        amount_currency: values.contributionCurrency,
      },
      enabled: false,
    });

    useEffect(() => {
      const shouldRefetch =
        (values.tip > 0 || values.sponsor_magazine > 0) &&
        values.contributionCurrency.toLowerCase() !== 'usd';

      if (shouldRefetch) {
        refetchMultipleCurrencyExchange();
      }
    }, [
      values.tip,
      values.sponsor_magazine,
      values.contributionCurrency,
      refetchMultipleCurrencyExchange,
    ]);

    const handlePreSubmit = () => {
      const MINIMUM_AMOUNT = 2;
      const MAXIMUM_AMOUNT = 999999.99;

      if (values.contributionAmount > MAXIMUM_AMOUNT && values.paymentMethod === 'debit_credit') {
        setFieldError(
          'contributionAmount',
          `Amount must be less than or equal to ${MAXIMUM_AMOUNT} ${values.contributionCurrency}`,
        );
        return;
      }

      if (values.tip > MAXIMUM_AMOUNT && values.paymentMethod === 'debit_credit') {
        setFieldError(
          'tip',
          `Amount must be less than or equal to ${MAXIMUM_AMOUNT} ${values.contributionCurrency}`,
        );
        return;
      }
      if (values.sponsor_magazine > MAXIMUM_AMOUNT && values.paymentMethod === 'debit_credit') {
        setFieldError(
          'sponsor_magazine',
          `Amount must be less than or equal to ${MAXIMUM_AMOUNT} ${values.contributionCurrency}`,
        );
        return;
      }

      const isTipInvalid =
        ((exchangeAmount?.tip?.amount ?? 0) > 0 &&
          (exchangeAmount?.tip.amount ?? 0) < MINIMUM_AMOUNT) ||
        (values.tip > 0 && values.tip < MINIMUM_AMOUNT);

      const isSponsorMagazineInvalid =
        ((exchangeAmount?.sponsorMagazine?.amount ?? 0) > 0 &&
          (exchangeAmount?.sponsorMagazine.amount ?? 0) < MINIMUM_AMOUNT) ||
        (values.sponsor_magazine > 0 && values.sponsor_magazine < MINIMUM_AMOUNT);

      if (isTipInvalid) {
        setFieldError('tip', `Minimum is ${MINIMUM_AMOUNT} USD`);
        return;
      }

      if (isSponsorMagazineInvalid) {
        setFieldError('sponsor_magazine', `Minimum is ${MINIMUM_AMOUNT} USD`);
        return;
      }
      handleSubmit();
    };

    const totalDontedValue = useMemo(() => {
      const total =
        Number(values.contributionAmount) + Number(values.tip) + Number(values.sponsor_magazine);
      return total?.toFixed(2);
    }, [values.contributionAmount, values.tip, values.sponsor_magazine]);

    if (isPublic && event && event.isRecuring) {
      return (
        <Card className="space-y-4 py-10 px-8 xxs:w-[300px] xs:w-[350px] sm:w-[380px] md:w-[700px] lg:w-[870px]">
          <p>You have to login to be able to subscribe to this event</p>
          <Button
            style="bg-gold text-white font-bold text-sm py-3 rounded px-3"
            onClick={() => navigate('/partner/auth/login')}
          >
            Login
          </Button>
        </Card>
      );
    }

    if (event && event.event_subscription?.status === 'paid') {
      return (
        <Card className="space-y-4 py-10 px-8 xxs:w-[300px] xs:w-[350px] sm:w-[380px] md:w-[700px] lg:w-[870px]">
          <p>You already subscribed to this event, to go profile to manage your subscription</p>
        </Card>
      );
    }
    return (
      <Card className="space-y-4 py-10 px-8 xxs:w-[300px] xs:w-[350px] sm:w-[380px] md:w-[700px] lg:w-[870px]">
        {event?.isRecuring ? (
          <div>
            <h1 className=" text-base font-semibold">Subscribe To Meeting</h1>
            <p className="mb-4  text-xs font-normal">
              {'You will get billed monthly as long as you are subscribed.'}
            </p>
          </div>
        ) : (
          <p className="text-base font-semibold">Register For Meeting</p>
        )}
        <div className="grid grid-cols-1 gap-4">
          <PaymentAmountField
            amountFieldName="contributionAmount"
            currencyFieldName="contributionCurrency"
            selectedCurrency={values.contributionCurrency}
            paymentMethod={values.paymentMethod ?? 'debit_credit'}
          />
          <TextField name="tip" label="Parach(optional)" type="number" required={false} />
          <TextField
            name="sponsor_magazine"
            label="Sponsor Magazine"
            type="number"
            required={false}
          />
        </div>
        <div className="flex w-full justify-end space-x-1 text-xs">
          <span>Total Amount:</span>
          <span className="underline decoration-double">{`${selectedMinDonation?.symbol} ${totalDontedValue}`}</span>
        </div>
        <div className="border-b-2 border-b-gray-300 pt-4" />

        <div className="my-2">
          <PrayerRequestCheckboxes fieldName="prayerRequests" />
        </div>
        <Button
          style={`${
            isLoaddingExchangeRate ? 'bg-light-gray' : 'bg-gold'
          } text-white font-bold text-sm py-3 rounded px-3`}
          isLoading={isSubmitting}
          isDisabled={isLoaddingExchangeRate}
          onClick={handlePreSubmit}
        >
          Contribute
        </Button>
      </Card>
    );
  };

  return roboCheck ? (
    <Form
      initialValues={initiaValues}
      onSubmitForm={onSubmitForm}
      render={FormComponents}
      validationSchema={schema}
    />
  ) : (
    <div className="my-2">
      <p>Verify you’re not a robot to continue</p>
      <RecaptchaComponent setRebotCheck={setRebotCheck} />
    </div>
  );
};
